import React from "react";
import { Star, MapPin, Briefcase, Languages, Zap, Calendar } from "lucide-react";
import VerifiedAdvocateBadge from "@user/components/VerifiedAdvocateBadge";
import { Button } from "@/components/ui/button";
import { specializationsOf } from "@shared/specializations";
import { Link } from "react-router-dom";
import PriceTag, { lawyerPricing } from "@user/components/PriceTag";
import { photoSrc } from "@shared/photoSrc";

export const LawyerCard = ({ lawyer, onBook, onInstant }) => {
  const displayName = lawyer.name || "Advoset Lawyer";
  const initials = displayName.split(" ").slice(-2).map((x) => x[0] || "").join("") || "AL";
  const available = !!(lawyer.instant_available ?? lawyer.available);
  const isPulsing = !!lawyer.pulse_until && new Date(lawyer.pulse_until).getTime() > Date.now();
  return (
    <div
      data-testid={`lawyer-card-${lawyer.id}`}
      className={`group bg-white border rounded-2xl p-4 shadow-sm hover:shadow-md transition-all duration-200 relative ${
        isPulsing ? "border-emerald-300 ring-1 ring-emerald-200" : "border-slate-200"
      }`}
    >
      {isPulsing && (
        <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" data-testid={`pulse-badge-${lawyer.id}`}>
          <span className="relative inline-flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-600" />
          </span>
          Live now
        </div>
      )}
      {!isPulsing && available && (
        <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" data-testid={`available-badge-${lawyer.id}`}>
          <span className="adv-pulse-dot" /> Available now
        </div>
      )}
      <div className="flex gap-3">
        {lawyer.photo ? (
          <img src={photoSrc(lawyer.photo, "sm")} alt={displayName} className="h-16 w-16 rounded-xl object-cover border border-slate-200 shrink-0" />
        ) : (
          <div className="h-16 w-16 rounded-xl adv-navy grid place-items-center font-bold text-lg shrink-0 text-[#D4AF37]">
            {initials}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-1.5">
            <div className="min-w-0">
              <div className="flex items-center gap-1">
                <h3 className="font-bold text-[#0A2540] text-base leading-tight" style={{ fontFamily: "Poppins" }}>{displayName}</h3>
                <VerifiedAdvocateBadge lawyer={lawyer} size="sm" />
              </div>
              <p className="text-xs text-slate-500 mt-0.5 truncate" data-testid="lawyer-card-specializations">{specializationsOf(lawyer).join(" · ") || lawyer.specialization}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-600 flex-wrap">
            <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded font-bold">
              <Star size={10} fill="currentColor" />{Number(lawyer.rating || 0).toFixed(1)}
            </span>
            <span className="inline-flex items-center gap-0.5"><MapPin size={11} />{lawyer.city || "India"}</span>
            <span className="inline-flex items-center gap-0.5"><Briefcase size={11} />{Number(lawyer.experience || 0)}y</span>
          </div>
          {lawyer.languages?.length > 0 && (
            <div className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-500 truncate">
              <Languages size={11} /> {lawyer.languages.slice(0, 3).join(" · ")}
            </div>
          )}
        </div>
      </div>
      {lawyer.bio && (
        <p className="mt-2.5 text-sm text-slate-600 line-clamp-2">{lawyer.bio}</p>
      )}
      <div className="mt-3 flex items-end justify-between gap-2 flex-wrap">
        <div>
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Chat from</span>
          <PriceTag pricing={lawyerPricing(lawyer, "chat")} layout="card" size="md" testid={`price-${lawyer.id}`} />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <Link to={`/lawyers/${lawyer.id}`}>
            <Button variant="outline" size="sm" data-testid={`view-profile-${lawyer.id}`} className="rounded-full border-slate-200 h-8 text-xs px-3">
              Profile
            </Button>
          </Link>
          {available ? (
            <>
              <Button
                onClick={() => onInstant?.(lawyer)}
                size="sm"
                data-testid={`instant-btn-${lawyer.id}`}
                className="rounded-full adv-btn-gold font-bold h-8 text-xs px-3"
                title="Start Instant Consultation"
              >
                <Zap size={12} fill="currentColor" /> Instant
              </Button>
              <Button
                onClick={() => onBook?.(lawyer)}
                size="sm"
                variant="outline"
                data-testid={`book-btn-${lawyer.id}`}
                className="rounded-full border-[#0A2540]/20 hover:bg-[#0A2540] hover:text-white font-semibold h-8 text-xs px-3"
                title="Book for Later"
              >
                <Calendar size={12} /> Later
              </Button>
            </>
          ) : (
            <Button
              onClick={() => onBook?.(lawyer)}
              size="sm"
              data-testid={`book-btn-${lawyer.id}`}
              className="rounded-full adv-btn-gold font-bold h-8 text-xs px-3"
              title="Book a slot"
            >
              <Calendar size={12} /> Book slot
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export const LawyerCardSkeleton = () => (
  <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
    <div className="flex gap-3">
      <div className="h-16 w-16 rounded-xl adv-skeleton shrink-0" />
      <div className="flex-1 space-y-1.5">
        <div className="h-4 rounded adv-skeleton w-2/3" />
        <div className="h-3 rounded adv-skeleton w-1/2" />
        <div className="h-3 rounded adv-skeleton w-1/3" />
      </div>
    </div>
    <div className="mt-3 space-y-1.5">
      <div className="h-3 rounded adv-skeleton w-full" />
      <div className="h-3 rounded adv-skeleton w-4/5" />
    </div>
    <div className="mt-3 flex items-end justify-between gap-2">
      <div className="space-y-1.5">
        <div className="h-2.5 rounded adv-skeleton w-14" />
        <div className="h-6 rounded adv-skeleton w-20" />
      </div>
      <div className="flex gap-1.5">
        <div className="h-8 rounded-full adv-skeleton w-16" />
        <div className="h-8 rounded-full adv-skeleton w-16" />
      </div>
    </div>
  </div>
);

export default LawyerCard;
